import { useQuery } from '@tanstack/react-query';
import {
  shiftInstancesApi,
  sectionsApi,
  reservationsApi,
  guestsApi,
  preshiftConfigApi,
  ShiftInstance,
  ShiftAssignment,
} from '../services/api';

export interface Section {
  id: string;
  name: string;
  color?: string;
  tables?: Array<{ id: string; number: string; capacity: number }>;
}

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
  skills?: string[];
  allergyTraining?: string[];
  sectionHistory?: Record<string, number>;
  // Compliance warnings for scheduling
  complianceWarnings?: string[];
  complianceBlockingReasons?: string[];
  hasComplianceIssues?: boolean;
}

export interface Guest {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  isVIP?: boolean;
  allergies?: string[];
  dietaryRestrictions?: string[];
  preferences?: {
    preferredServer?: string;
    preferredSection?: string;
    [key: string]: unknown;
  };
}

export interface Reservation {
  id: string;
  guestName: string;
  guestId?: string;
  guestPhone?: string;
  guestEmail?: string;
  partySize: number;
  date: string;
  time: string;
  tableId?: string;
  tableIds?: string[];
  notes?: string;
  status?: string;
}

export interface Special {
  id: string;
  menuItemId: string;
  date: string;
  specialPriceCents?: number;
  description?: string;
  regularPriceCents?: number;
  staffDescription?: string;
  allergenInfo?: string;
  ingredientsList?: string;
  pairingSuggestions?: string[];
  prepNotes?: string;
  menuItem?: {
    id: string;
    name: string;
    priceCents: number;
    staffDescription?: string;
    allergenInfo?: string;
    ingredientsList?: string;
    pairingSuggestions?: string[];
    prepNotes?: string;
  };
}

export interface Announcement {
  id: string;
  title: string;
  message: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  targetRoles?: string[];
  isActive: boolean;
}

export interface EightySixItem {
  id: string;
  name: string;
  isAvailable: boolean;
  availableQuantity?: number;
  staffDescription?: string;
  allergenInfo?: string;
  ingredientsList?: string;
  prepNotes?: string;
  pairingSuggestions?: string[];
}

export interface ShiftSetupData {
  shift: ShiftInstance | null;
  sections: Section[];
  employees: Employee[];
  assignments: ShiftAssignment[];
  reservations: Reservation[];
  shiftReservations: Reservation[]; // Filtered to shift time window
  guests: Guest[];
  specials: Special[];
  eightySixItems: EightySixItem[];
  announcements: Announcement[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useShiftSetupData(instanceId: string | undefined): ShiftSetupData {
  // Phase 1: Shift instance details
  const shiftQuery = useQuery({
    queryKey: ['shift-instance', instanceId],
    queryFn: async () => {
      if (!instanceId) return null;
      const response = await shiftInstancesApi.getById(instanceId);
      return response.data?.data || response.data;
    },
    enabled: !!instanceId,
  });

  // Phase 1b: Sections
  const sectionsQuery = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  // Phase 2: Available employees for this shift
  const employeesQuery = useQuery({
    queryKey: ['shift-available-employees', instanceId],
    queryFn: async () => {
      if (!instanceId) return [];
      const response = await shiftInstancesApi.getAvailableEmployees(instanceId);
      return response.data?.data || response.data || [];
    },
    enabled: !!instanceId,
  });

  // Phase 2b: Current assignments
  const assignmentsQuery = useQuery({
    queryKey: ['shift-assignments', instanceId],
    queryFn: async () => {
      if (!instanceId) return [];
      const response = await shiftInstancesApi.getAssignments(instanceId);
      return response.data?.data || response.data || [];
    },
    enabled: !!instanceId,
  });

  // Phase 3: Reservations for the shift date
  const shiftDate = shiftQuery.data?.date;
  // Normalize date to YYYY-MM-DD format for API calls
  const normalizedDate = shiftDate
    ? (typeof shiftDate === 'string' ? shiftDate.split('T')[0] : new Date(shiftDate).toISOString().split('T')[0])
    : null;

  const reservationsQuery = useQuery({
    queryKey: ['reservations', normalizedDate],
    queryFn: async () => {
      if (!normalizedDate) return [];
      try {
        const response = await reservationsApi.getAll({ date: normalizedDate });
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch reservations:', error);
        return [];
      }
    },
    enabled: !!normalizedDate,
  });

  // Phase 3b: Guests (for VIP info, allergies, preferences)
  const guestsQuery = useQuery({
    queryKey: ['guests-all'],
    queryFn: async () => {
      const response = await guestsApi.getAll({ limit: 500 });
      return response.data?.data || response.data || [];
    },
  });

  // Phase 4: Menu setup data
  const specialsQuery = useQuery({
    queryKey: ['specials', normalizedDate],
    queryFn: async () => {
      if (!normalizedDate) return [];
      try {
        const response = await preshiftConfigApi.getSpecials(normalizedDate);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch specials:', error);
        return [];
      }
    },
    enabled: !!normalizedDate,
  });

  const eightySixQuery = useQuery({
    queryKey: ['eighty-six'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getEightySixItems();
      // The API returns { eightySixItems: [...], availableItems: [...] }
      const data = response.data?.data;
      if (data?.eightySixItems) {
        // Transform to expected format - include both 86'd and available items
        return [
          ...data.eightySixItems.map((item: any) => ({ ...item, isAvailable: false })),
          ...data.availableItems.map((item: any) => ({ ...item, isAvailable: true })),
        ];
      }
      return response.data?.data || response.data || [];
    },
  });

  const announcementsQuery = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getAnnouncements();
      return response.data?.data || response.data || [];
    },
  });

  // Filter reservations to shift time window
  const shift = shiftQuery.data as ShiftInstance | null;
  const reservations = (reservationsQuery.data || []) as Reservation[];

  const shiftReservations = shift
    ? reservations.filter((res) => {
        if (!res.time) return false;
        const resDateTime = new Date(`${res.date}T${res.time}`);
        const shiftStart = new Date(shift.startTime);
        const shiftEnd = new Date(shift.endTime);
        return resDateTime >= shiftStart && resDateTime <= shiftEnd;
      })
    : [];

  // Combine loading/error states
  const isLoading =
    shiftQuery.isLoading ||
    sectionsQuery.isLoading ||
    employeesQuery.isLoading ||
    assignmentsQuery.isLoading ||
    reservationsQuery.isLoading ||
    guestsQuery.isLoading ||
    specialsQuery.isLoading ||
    eightySixQuery.isLoading ||
    announcementsQuery.isLoading;

  const isError =
    shiftQuery.isError ||
    sectionsQuery.isError ||
    employeesQuery.isError ||
    assignmentsQuery.isError;

  const error =
    shiftQuery.error ||
    sectionsQuery.error ||
    employeesQuery.error ||
    assignmentsQuery.error;

  const refetch = () => {
    shiftQuery.refetch();
    sectionsQuery.refetch();
    employeesQuery.refetch();
    assignmentsQuery.refetch();
    reservationsQuery.refetch();
    guestsQuery.refetch();
    specialsQuery.refetch();
    eightySixQuery.refetch();
    announcementsQuery.refetch();
  };

  return {
    shift,
    sections: (sectionsQuery.data || []) as Section[],
    employees: (employeesQuery.data || []) as Employee[],
    assignments: (assignmentsQuery.data || []) as ShiftAssignment[],
    reservations,
    shiftReservations,
    guests: (guestsQuery.data || []) as Guest[],
    specials: (specialsQuery.data || []) as Special[],
    eightySixItems: (eightySixQuery.data || []) as EightySixItem[],
    announcements: (announcementsQuery.data || []) as Announcement[],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}
